import {InvalidParamsSharedError} from '../../shared/Errors/InvalidParamsSharedError.js'
import AdsErrorsFactory from '../Errors/factory.js'
import {AdAggregationListValueObject} from './AdAggregationListValueObject.js'
import {AdAggregationsListValueObject} from './AdAggregationsListValueObject.js'
import {AdAggregationsValueObject} from './AdAggregationsValueObject.js'
import {AdAggregationValueObject} from './AdAggregationValueObject.js'
import AdsListValueObjectLegacy from './AdsListValueObjectLegacy.js'
import AdsSearchParamsValueObject from './AdsSearchParamsValueObject.js'
import {AdStatsValueObject} from './AdStatsValueObject.js'
import {AdTypeValueObject} from './AdTypeValueObject.js'
import {LastDetailsViewedItemValueObject} from './LastDetailsViewedItemValueObject.js'
import {LastDetailsViewedListValueObject} from './LastDetailsViewedListValueObject.js'
import {MetadataRecommendedAdsValueObject} from './MetadataRecommendedAdsValueObject.js'
import {RecommendedAdsListValueObjectFactory} from './RecommendedAdsListValueObjectFactory.js'
import RelatedAdsRequestValueObject from './RelatedAdsRequestValueObject.js'
import ReportAdsRequestValueObject from './ReportAdsRequestValueObject.js'
import SearchDataAdsValueObject from './SearchDataAdsValueObject.js'
import SearchesListValueObject from './SearchesListValueObject.js'
import SearchItemValueObject from './SearchItemValueObject.js'
import {SendComplaintAdsRequestValueObject} from './SendComplaintAdsRequestValueObject.js'
import {StatsRequestValueObject} from './StatsRequestValueObject.js'
import {StatsResponseValueObject} from './StatsResponseValueObject.js'
import SubscriptionAdsListValueObject from './SubscriptionAdsListValueObject.js'
import {SubscriptionCommitmentOptionsValueObject} from './SubscriptionCommitmentOptionsValueObject.js'
import {WdylAdsValueObject} from './WdylAdsValueObject.js'

export default class AdsValueObjectsFactory {
  /**
   * @method
   * @param {Object} deps
   * @param {String|Number} deps.category1Id
   * @param {String|Number} deps.category2Id
   * @param {String|Number} deps.category3Id
   * @param {String|Number} deps.offerTypeId
   * @return {AdTypeValueObject}
   */
  static adTypeValueObject = ({category1Id, category2Id, category3Id, offerTypeId}) =>
    new AdTypeValueObject({category1Id, category2Id, category3Id, offerTypeId})

  /**
   * @param {Object} deps
   * @param {Array} deps.bodyTypes
   * @param {Number} deps.fuelType
   * @param {Number} deps.kmsMax
   * @param {Number} deps.kmsMin
   * @param {Number} deps.model
   * @param {Number} deps.brand
   * @param {Number} deps.priceMax
   * @param {Number} deps.province
   * @param {Number} deps.yearMax
   * @param {Number} deps.yearMin
   * @param {String} deps.keywords
   * @param {String} deps.version
   * @return {AdsSearchParamsValueObject}
   */
  static adsSearchParamsValueObject = ({
    bodyTypes,
    fuelType,
    kmsMax,
    kmsMin,
    model,
    brand,
    priceMax,
    province,
    yearMax,
    yearMin,
    keywords,
    version
  } = {}) =>
    new AdsSearchParamsValueObject({
      bodyTypes,
      fuelType,
      kmsMax,
      kmsMin,
      model,
      brand,
      priceMax,
      province,
      yearMax,
      yearMin,
      keywords,
      version
    })

  /**
   * @method
   * @param {Object} deps
   * @param {Object} deps.highlightedAd
   * @param {Array} deps.items
   * @param {Number} deps.totalPages
   * @param {Number} deps.totalResults
   * @return {AdsListValueObjectLegacy}
   */
  static adsListValueObject = ({aggregations, highlightedAd, items, totalPages, totalResults}) =>
    new AdsListValueObjectLegacy({
      aggregations,
      highlightedAd,
      items,
      totalPages,
      totalResults
    })

  static sendComplaintAdsRequestValueObject = ({adId, complaintTypeId, comments, email, name}) => {
    SendComplaintAdsRequestValueObject.validate({
      adId,
      complaintTypeId,
      invalidParamsError: InvalidParamsSharedError
    })
    return new SendComplaintAdsRequestValueObject({
      adId,
      complaintTypeId,
      comments,
      email,
      name
    })
  }

  static RelatedAdsRequestValueObject = ({
    adId,
    count,
    modelId,
    offerTypeId,
    preferredContractModes,
    subscriptionContext
  }) =>
    new RelatedAdsRequestValueObject({
      adId,
      count,
      modelId,
      offerTypeId,
      preferredContractModes,
      subscriptionContext
    })

  /**
   * @method
   * @param {Object} params
   * @param {Number} params.adId
   * @param {Number} params.queryTypeId
   * @param {String} params.description
   * @param {String} params.captchaToken
   * @return {ReportAdsRequestValueObject}
   */
  static reportAdsRequest = ({adId, queryTypeId, description, captchaToken}) => {
    ReportAdsRequestValueObject.validate({
      adId,
      queryTypeId,
      invalidError: AdsErrorsFactory.invalidRequestError
    })
    return new ReportAdsRequestValueObject({
      adId,
      queryTypeId,
      description,
      captchaToken
    })
  }

  static searchDataAdsValueObject = ({breadcrumbs, canonical, listingSectionId, metaData, search} = {}) => {
    return new SearchDataAdsValueObject({
      breadcrumbs,
      canonical,
      listingSectionId,
      metaData,
      search
    })
  }

  static searchesListValueObject = ({items}) => new SearchesListValueObject({items})

  static searchItemValueObject = ({title, url}) =>
    new SearchItemValueObject({
      title,
      url
    })

  /**
   * @method
   * @param {Object} deps
   * @param {Array} deps.items
   * @param {Number} deps.totalPages
   * @param {Number} deps.totalResults
   * @return {SubscriptionAdsListValueObject}
   */
  static subscriptionAdsListValueObject = ({items, totalPages, totalResults}) =>
    new SubscriptionAdsListValueObject({items, totalPages, totalResults})

  static lastDetailsViewedListValueObject = ({items}) => new LastDetailsViewedListValueObject({items})

  static lastDetailsViewedItemValueObject = ({id}) => new LastDetailsViewedItemValueObject({id})

  static subscriptionCommitmentOptionsValueObject = ({hasNoCommitmentOption, commitmentMonths}) =>
    new SubscriptionCommitmentOptionsValueObject({
      hasNoCommitmentOption,
      commitmentMonths
    })

  static adAggregationValueObject = ({id, totalResults}) => new AdAggregationValueObject({id, totalResults})

  static adAggregationListValueObject = ({items}) => new AdAggregationListValueObject({items})

  static adAggregationsValueObject = ({filterName, items}) => new AdAggregationsValueObject({filterName, items})

  static adAggregationsListValueObject = ({items}) => new AdAggregationsListValueObject({items})

  static statsRequestValueObject = ({filters, aggregations, pagination}) =>
    new StatsRequestValueObject({filters, aggregations, pagination})

  static statsResponseValueObject = ({stats}) => new StatsResponseValueObject({stats})

  static wdylAdsValueObject = ({newVehiclesValueObject, rentingVehiclesValueObject}) =>
    new WdylAdsValueObject({
      newVehicles: newVehiclesValueObject,
      rentingVehicles: rentingVehiclesValueObject
    })

  static adStatsValueObject = ({favorites, views}) => new AdStatsValueObject({favorites, views})

  static recommendedAdsListValueObjectFactory = ({items, metadata, isRecommendedAdsFallback}) =>
    new RecommendedAdsListValueObjectFactory({items, metadata, isRecommendedAdsFallback})

  static metaDataRecommendedAdsValueObject = ({itemId, listName, recommendationId, isEmpty}) =>
    new MetadataRecommendedAdsValueObject({
      itemId,
      listName,
      recommendationId,
      isEmpty
    })
}
